import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getServiceReport } from "./serviceReportService";
import { FaChartBar, FaFileCsv, FaPrint, FaArrowLeft, FaExclamationTriangle, FaFilter, FaUndo } from "react-icons/fa";
import "./ServiceReport.css";
import { usePermissions } from "../../context/PermissionContext";
import AccessDenied from "../ui/AccessDenied";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/** Return the CSS class suffix for service-type badges */
const badgeClass = (type) => {
  const known = [
    "Flights",
    "Hotels",
    "Visa",
    "Insurance",
    "Tours",
    "Transfers",
    "Cruise",
    "CarRental",
    "Package",
  ];
  const cleaned = (type || "").replace(/\s+/g, "");
  return known.includes(cleaned) ? `type-${cleaned}` : "type-default";
};

/** Return margin badge colour class */
const marginClass = (pct) => {
  if (pct >= 20) return "good";
  if (pct >= 10) return "medium";
  return "low";
};

const ServiceReportPage = () => {
  const navigate = useNavigate();
  const { canView, canExport } = usePermissions();

  if (!canView("service")) {
    return <AccessDenied moduleName="Service Report" action="view" />;
  }

  // ── Filters ─────────────────────────────────────────────
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [customerName, setCustomerName] = useState("");

  // ── Data ────────────────────────────────────────────────
  const [summary, setSummary] = useState(null);
  const [breakdown, setBreakdown] = useState([]);
  const [items, setItems] = useState([]);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── View toggle ─────────────────────────────────────────
  const [view, setView] = useState("breakdown"); // "breakdown" | "detail"

  const hasFetched = useRef(false);

  // ── Fetch ───────────────────────────────────────────────
  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        service_type: serviceType || undefined,
        customer_name: customerName || undefined,
      };
      const data = await getServiceReport(filters);
      setSummary(data.summary || null);
      setBreakdown(data.service_breakdown || []);
      setItems(data.items || []);
      setAvailableTypes(data.available_service_types || []);
    } catch (err) {
      console.error("Service Report fetch error:", err);
      setError("Failed to load the Service Report. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, serviceType, customerName]);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchReport();
    }
  }, [fetchReport]);

  // ── Reset ───────────────────────────────────────────────
  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    setServiceType("");
    setCustomerName("");
    hasFetched.current = false;
    // fetchReport will be called by the useEffect via hasFetched
    fetchReport();
  };

  // ── Print ───────────────────────────────────────────────
  const handlePrint = () => window.print();

  // ── Export to CSV ───────────────────────────────────────
  const handleExportCSV = () => {
    const rows =
      view === "breakdown"
        ? [
            [
              "Service Type",
              "Count",
              "Revenue",
              "Cost",
              "Profit",
              "Margin %",
              "Revenue Share %",
            ],
            ...breakdown.map((b) => [
              b.service_type,
              b.count,
              b.total_revenue,
              b.total_cost,
              b.total_profit,
              b.margin_pct,
              b.revenue_share_pct,
            ]),
          ]
        : [
            [
              "Invoice",
              "Customer",
              "Date",
              "Service Type",
              "Item",
              "Qty",
              "Cost",
              "Selling",
              "Total",
              "Profit",
              "Margin %",
            ],
            ...items.map((i) => [
              i.invoice_no,
              i.customer_name,
              i.sales_date,
              i.service_type,
              i.item_name,
              i.quantity,
              i.cost_price,
              i.selling_price,
              i.total_amount,
              i.profit,
              i.margin_pct,
            ]),
          ];

    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `service_report_${view}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="service-report-page">
      {/* ── Header ──────────────────────────────────────── */}
      <div className="service-report-header">
        <h2><FaChartBar style={{ color: "#0284c7" }} className="me-2" /> Service Report</h2>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button className="sr-btn-print" onClick={handleExportCSV}>
            <FaFileCsv className="me-1" /> Export CSV
          </button>
          <button className="sr-btn-print" onClick={handlePrint}>
            🖨 Print
          </button>
          <button
            className="sr-btn-reset"
            onClick={() => navigate("/dashboard")}
          >
            ← Dashboard
          </button>
        </div>
      </div>

      {/* ── Summary Cards ───────────────────────────────── */}
      {summary && (
        <div className="service-report-summary">
          <div className="sr-card revenue">
            <div className="sr-label">Total Revenue</div>
            <div className="sr-value">AED {fmt(summary.total_revenue)}</div>
          </div>
          <div className="sr-card cost">
            <div className="sr-label">Total Cost</div>
            <div className="sr-value">AED {fmt(summary.total_cost)}</div>
          </div>
          <div className="sr-card profit">
            <div className="sr-label">Total Profit</div>
            <div className="sr-value">AED {fmt(summary.total_profit)}</div>
          </div>
          <div className="sr-card txn">
            <div className="sr-label">Transactions</div>
            <div className="sr-value">{summary.total_transactions}</div>
          </div>
          <div className="sr-card margin">
            <div className="sr-label">Overall Margin</div>
            <div className="sr-value">{summary.margin_pct}%</div>
          </div>
        </div>
      )}

      {/* ── Filters ─────────────────────────────────────── */}
      <div className="service-report-filters">
        <div className="filter-group">
          <label>Date From</label>
          <input
            id="sr-start-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>Date To</label>
          <input
            id="sr-end-date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>Service Type</label>
          <select
            id="sr-service-type"
            value={serviceType}
            onChange={(e) => setServiceType(e.target.value)}
          >
            <option value="">All Types</option>
            {availableTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Customer</label>
          <input
            id="sr-customer-name"
            type="text"
            placeholder="Search customer…"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
          />
        </div>

        <button id="sr-apply-btn" className="sr-btn-filter" onClick={fetchReport}>
          Apply Filters
        </button>
        <button id="sr-reset-btn" className="sr-btn-reset" onClick={handleReset}>
          Reset
        </button>
      </div>

      {/* ── View Toggle ─────────────────────────────────── */}
      <div style={{ marginBottom: "16px" }}>
        <div className="sr-view-toggle">
          <button
            className={view === "breakdown" ? "active" : ""}
            onClick={() => setView("breakdown")}
          >
            By Service Type
          </button>
          <button
            className={view === "detail" ? "active" : ""}
            onClick={() => setView("detail")}
          >
            Item Detail
          </button>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────── */}
      {loading ? (
        <div className="sr-loading">
          <div className="spinner" />
          Loading service report…
        </div>
      ) : error ? (
        <div className="sr-loading text-danger">
          <FaExclamationTriangle className="me-2" /> {error}
        </div>
      ) : view === "breakdown" ? (
        /* ── Breakdown View ───────────────────────────────── */
        breakdown.length === 0 ? (
          <div className="sr-empty">
            No service data found for the selected filters.
          </div>
        ) : (
          <div className="sr-breakdown-card">
            <div className="sr-section-header">
              <strong>Service Type Breakdown</strong>
              <span>
                {breakdown.length} service type
                {breakdown.length !== 1 ? "s" : ""}
              </span>
            </div>
            <div className="sr-table-wrapper">
              <table className="sr-table">
                <thead>
                  <tr>
                    <th>Service Type</th>
                    <th className="num">Count</th>
                    <th className="num">Revenue (AED)</th>
                    <th className="num">Cost (AED)</th>
                    <th className="num">Profit (AED)</th>
                    <th className="num">Margin</th>
                    <th>Revenue Share</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdown.map((b) => (
                    <tr key={b.service_type}>
                      <td>
                        <span
                          className={`sr-service-badge ${badgeClass(
                            b.service_type
                          )}`}
                        >
                          {b.service_type}
                        </span>
                      </td>
                      <td className="num">{b.count}</td>
                      <td className="num">{fmt(b.total_revenue)}</td>
                      <td className="num">{fmt(b.total_cost)}</td>
                      <td className="num">
                        <span
                          className={
                            b.total_profit >= 0
                              ? "sr-profit-positive"
                              : "sr-profit-negative"
                          }
                        >
                          {fmt(b.total_profit)}
                        </span>
                      </td>
                      <td className="num">
                        <span
                          className={`sr-margin-badge ${marginClass(
                            b.margin_pct
                          )}`}
                        >
                          {b.margin_pct}%
                        </span>
                      </td>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <div className="sr-bar-container">
                            <div
                              className="sr-bar-fill"
                              style={{ width: `${b.revenue_share_pct}%` }}
                            />
                          </div>
                          <span style={{ fontSize: "0.78rem", color: "#6b7280" }}>
                            {b.revenue_share_pct}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>
                      <strong>Total</strong>
                    </td>
                    <td className="num">
                      {breakdown.reduce((a, b) => a + b.count, 0)}
                    </td>
                    <td className="num">
                      {fmt(
                        breakdown.reduce((a, b) => a + b.total_revenue, 0)
                      )}
                    </td>
                    <td className="num">
                      {fmt(
                        breakdown.reduce((a, b) => a + b.total_cost, 0)
                      )}
                    </td>
                    <td className="num">
                      {fmt(
                        breakdown.reduce((a, b) => a + b.total_profit, 0)
                      )}
                    </td>
                    <td className="num">
                      {summary ? summary.margin_pct : 0}%
                    </td>
                    <td>100%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )
      ) : /* ── Detail View ──────────────────────────────────── */
      items.length === 0 ? (
        <div className="sr-empty">
          No service items found for the selected filters.
        </div>
      ) : (
        <div className="sr-breakdown-card">
          <div className="sr-section-header">
            <strong>Item Detail</strong>
            <span>
              {items.length} item{items.length !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="sr-table-wrapper">
            <table className="sr-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Invoice</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>Service</th>
                  <th>Item</th>
                  <th className="num">Qty</th>
                  <th className="num">Cost</th>
                  <th className="num">Selling</th>
                  <th className="num">Total</th>
                  <th className="num">Profit</th>
                  <th className="num">Margin</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i, idx) => (
                  <tr key={i.id}>
                    <td style={{ color: "#9ca3af", fontSize: "0.78rem" }}>
                      {idx + 1}
                    </td>
                    <td style={{ fontWeight: 500 }}>{i.invoice_no || "—"}</td>
                    <td>{i.customer_name}</td>
                    <td>{i.sales_date || "—"}</td>
                    <td>
                      <span
                        className={`sr-service-badge ${badgeClass(
                          i.service_type
                        )}`}
                      >
                        {i.service_type}
                      </span>
                    </td>
                    <td>{i.item_name}</td>
                    <td className="num">{i.quantity}</td>
                    <td className="num">{fmt(i.cost_price)}</td>
                    <td className="num">{fmt(i.selling_price)}</td>
                    <td className="num">{fmt(i.total_amount)}</td>
                    <td className="num">
                      <span
                        className={
                          i.profit >= 0
                            ? "sr-profit-positive"
                            : "sr-profit-negative"
                        }
                      >
                        {fmt(i.profit)}
                      </span>
                    </td>
                    <td className="num">
                      <span
                        className={`sr-margin-badge ${marginClass(
                          i.margin_pct
                        )}`}
                      >
                        {i.margin_pct}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="6">
                    <strong>Grand Total</strong>
                  </td>
                  <td className="num">
                    {items.reduce((a, i) => a + i.quantity, 0)}
                  </td>
                  <td className="num">
                    {fmt(items.reduce((a, i) => a + i.cost_price, 0))}
                  </td>
                  <td className="num">
                    {fmt(items.reduce((a, i) => a + i.selling_price, 0))}
                  </td>
                  <td className="num">
                    {fmt(items.reduce((a, i) => a + i.total_amount, 0))}
                  </td>
                  <td className="num">
                    {fmt(items.reduce((a, i) => a + i.profit, 0))}
                  </td>
                  <td className="num">
                    {summary ? summary.margin_pct : 0}%
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceReportPage;
